/**
 * OpenDork local vault — AES-GCM content encryption with a session key.
 *
 * Unlock strategies:
 * 1. NIP-44 wrap-to-self (preferred when signer supports nip44)
 * 2. Passphrase (PBKDF2) for export bundles or when NIP-44 unavailable
 *
 * Sensitive strings are sealed as:  §OD1§<ivB64>.<ctB64>
 */

import {
  aesDecrypt,
  aesEncrypt,
  base64ToBytes,
  bytesToBase64,
  deriveKeyFromPassphrase,
  exportRawKey,
  generateAesKey,
  importRawKey,
} from './aes';

export const SEAL_PREFIX = '§OD1§';

export interface VaultMeta {
  version: 1;
  /** Owner pubkey (hex) when identity-bound */
  pubkey?: string;
  /** NIP-44 ciphertext of base64(raw vault key), encrypt-to-self */
  nip44WrappedKey?: string;
  /** Passphrase wrap: salt + iv + ct of raw vault key (all base64) */
  passphraseSalt?: string;
  passphraseIv?: string;
  passphraseCt?: string;
  createdAt: number;
  updatedAt: number;
}

export interface Nip44Signer {
  nip44?: {
    encrypt(pubkey: string, plaintext: string): Promise<string>;
    decrypt(pubkey: string, ciphertext: string): Promise<string>;
  };
}

let sessionKey: CryptoKey | null = null;
let sessionPubkey: string | null = null;
/**
 * True when workspace settings include a vault wrap.
 * Used for fail-closed seal: never write plaintext sensitive fields
 * while a vault is configured but the session key is missing.
 */
let vaultConfiguredFlag = false;

export function isVaultUnlocked(): boolean {
  return sessionKey !== null;
}

export function getVaultPubkey(): string | null {
  return sessionPubkey;
}

/** Call when loading/persisting vault meta so seal can fail closed. */
export function setVaultConfigured(on: boolean): void {
  vaultConfiguredFlag = on;
}

export function isVaultConfigured(): boolean {
  return vaultConfiguredFlag;
}

export function lockVault(): void {
  sessionKey = null;
  sessionPubkey = null;
  sealEnabled = true;
  // vaultConfiguredFlag intentionally preserved across lock
}

export function isSealed(value: string | undefined | null): boolean {
  return typeof value === 'string' && value.startsWith(SEAL_PREFIX);
}

/**
 * Local encryption policy (`encryptLocal`). When true and vault unlocked,
 * sealString encrypts. Cleared when encryption is disabled.
 * Callers still pass data through openString for sealed payloads.
 */
let sealEnabled = true;

export function setSealEnabled(on: boolean): void {
  sealEnabled = on;
}

/** Whether the encryptLocal policy currently allows sealing. */
export function isSealEnabled(): boolean {
  return sealEnabled;
}

/**
 * Encrypt a string with the session vault key.
 * Fail-closed: if a vault is configured and seal policy is on but the session
 * is locked, throw instead of writing plaintext (prevents accidental unseal).
 */
export async function sealString(plaintext: string): Promise<string> {
  if (plaintext === '' || isSealed(plaintext)) return plaintext;
  if (sessionKey && sealEnabled) {
    const { iv, ct } = await aesEncrypt(sessionKey, plaintext);
    return `${SEAL_PREFIX}${iv}.${ct}`;
  }
  if (vaultConfiguredFlag && sealEnabled && !sessionKey) {
    throw new Error(
      'Vault is locked — refused to write sensitive data as plaintext. Unlock and retry.',
    );
  }
  // No vault / seal policy off: plaintext pass-through
  return plaintext;
}

/** Decrypt a sealed string. Plaintext pass-through. Throws if sealed but locked. */
export async function openString(value: string): Promise<string> {
  if (!isSealed(value)) return value;
  if (!sessionKey) {
    throw new Error('Vault is locked — cannot decrypt local data');
  }
  const payload = value.slice(SEAL_PREFIX.length);
  const dot = payload.indexOf('.');
  if (dot === -1) throw new Error('Corrupt sealed payload');
  const iv = payload.slice(0, dot);
  const ct = payload.slice(dot + 1);
  return aesDecrypt(sessionKey, iv, ct);
}

/** Best-effort open; returns placeholder if locked. */
export async function openStringSafe(
  value: string,
  lockedPlaceholder = '[encrypted]',
): Promise<string> {
  if (!isSealed(value)) return value;
  if (!sessionKey) return lockedPlaceholder;
  try {
    return await openString(value);
  } catch {
    return lockedPlaceholder;
  }
}

export async function sealJson<T>(value: T): Promise<string> {
  return sealString(JSON.stringify(value));
}

export async function openJson<T>(value: string): Promise<T> {
  const plain = await openString(value);
  return JSON.parse(plain) as T;
}

// ─── Unlock / create ───

export async function createVaultWithNip44(
  signer: Nip44Signer,
  pubkey: string,
): Promise<VaultMeta> {
  if (!signer.nip44?.encrypt) {
    throw new Error('Signer does not support NIP-44 encryption');
  }
  const key = await generateAesKey();
  const raw = await exportRawKey(key);
  const rawB64 = bytesToBase64(raw);
  const wrapped = await signer.nip44.encrypt(pubkey, rawB64);
  sessionKey = key;
  sessionPubkey = pubkey;
  vaultConfiguredFlag = true;
  const now = Date.now();
  return {
    version: 1,
    pubkey,
    nip44WrappedKey: wrapped,
    createdAt: now,
    updatedAt: now,
  };
}

export async function unlockVaultWithNip44(
  meta: VaultMeta,
  signer: Nip44Signer,
  pubkey: string,
): Promise<void> {
  if (!meta.nip44WrappedKey) throw new Error('No NIP-44 wrapped vault key');
  if (!signer.nip44?.decrypt) {
    throw new Error('Signer does not support NIP-44 decryption');
  }
  // Bind vault to identity when known
  if (meta.pubkey && meta.pubkey !== pubkey) {
    throw new Error(
      'This vault belongs to a different Nostr account. Switch back to the original account or clear local OpenDork data.',
    );
  }
  const rawB64 = await signer.nip44.decrypt(pubkey, meta.nip44WrappedKey);
  const raw = base64ToBytes(rawB64);
  sessionKey = await importRawKey(raw);
  sessionPubkey = pubkey;
  vaultConfiguredFlag = true;
}

export async function createVaultWithPassphrase(passphrase: string): Promise<VaultMeta> {
  if (passphrase.length < 12) {
    throw new Error('Passphrase must be at least 12 characters');
  }
  const key = await generateAesKey();
  const raw = await exportRawKey(key);
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const wrapKey = await deriveKeyFromPassphrase(passphrase, salt);
  const { iv, ct } = await aesEncrypt(wrapKey, bytesToBase64(raw));
  sessionKey = key;
  sessionPubkey = null;
  vaultConfiguredFlag = true;
  const now = Date.now();
  return {
    version: 1,
    passphraseSalt: bytesToBase64(salt),
    passphraseIv: iv,
    passphraseCt: ct,
    createdAt: now,
    updatedAt: now,
  };
}

export async function unlockVaultWithPassphrase(
  meta: VaultMeta,
  passphrase: string,
  /** Optional: current login pubkey — rejected if vault is bound to another. */
  expectedPubkey?: string,
): Promise<void> {
  if (!meta.passphraseSalt || !meta.passphraseIv || !meta.passphraseCt) {
    throw new Error('No passphrase-wrapped vault key');
  }
  if (meta.pubkey && expectedPubkey && meta.pubkey !== expectedPubkey) {
    throw new Error(
      'This vault belongs to a different Nostr account. Switch back to the original account or clear local OpenDork data.',
    );
  }
  const salt = base64ToBytes(meta.passphraseSalt);
  const wrapKey = await deriveKeyFromPassphrase(passphrase, salt);
  const rawB64 = await aesDecrypt(wrapKey, meta.passphraseIv, meta.passphraseCt);
  sessionKey = await importRawKey(base64ToBytes(rawB64));
  sessionPubkey = meta.pubkey ?? expectedPubkey ?? null;
  vaultConfiguredFlag = true;
}

/** Encrypt arbitrary JSON with a one-off passphrase (export bundles). */
export async function encryptWithPassphrase(
  plaintext: string,
  passphrase: string,
): Promise<{ salt: string; iv: string; ct: string; v: 1 }> {
  if (passphrase.length < 12) {
    throw new Error('Passphrase must be at least 12 characters');
  }
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const key = await deriveKeyFromPassphrase(passphrase, salt);
  const { iv, ct } = await aesEncrypt(key, plaintext);
  return { v: 1, salt: bytesToBase64(salt), iv, ct };
}

export async function decryptWithPassphrase(
  bundle: { salt: string; iv: string; ct: string },
  passphrase: string,
): Promise<string> {
  const salt = base64ToBytes(bundle.salt);
  const key = await deriveKeyFromPassphrase(passphrase, salt);
  return aesDecrypt(key, bundle.iv, bundle.ct);
}

/** Seal many strings (parallel). */
export async function sealAll(values: string[]): Promise<string[]> {
  return Promise.all(values.map((v) => sealString(v)));
}

export async function openAll(values: string[]): Promise<string[]> {
  return Promise.all(values.map((v) => openString(v)));
}
